// client/guest/components/SubkindDetailsBlock.tsx
import React, { useEffect, useState } from "react";
import { TicketKind, isSubDisabled, fmtMoney } from "../utils";

type SubkindDetailsProps = {
  eventId?: number | null;
  kind: TicketKind | null;
  selectedSubId: number | null;
  currency: string;
  onSelectSub: (id: number) => void;
  onNext: () => void; // перейти к оплате
  onBack?: () => void; // опционально, пробрасываем из GuestFlow
  onHome?: () => void; // опционально, пробрасываем из GuestFlow
};

export default function SubkindDetailsBlock({
  eventId,
  kind,
  selectedSubId,
  currency,
  onSelectSub,
  onNext,
  onBack,
  onHome,
}: SubkindDetailsProps) {
  if (!kind) return null;
  const subs = kind.subkinds || [];

  // локальный выбранный id, инициализируем из selectedSubId
  const [selId, setSelId] = useState<number | null>(
    selectedSubId ?? (subs[0]?.id ?? null)
  );

  useEffect(() => {
    if (selectedSubId != null) setSelId(selectedSubId);
    else if (!selId && subs[0]) setSelId(subs[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubId, subs.length]);

  const current = subs.find((s) => s.id === selId) || null;
  const isLong = (s?: string | null) => (s?.trim()?.length ?? 0) > 18;

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-3">
      <div className="text-[13px] text-white/75">Подвиды • {kind.title}</div>

      <div className="grid gap-2 grid-cols-1 sm:grid-cols-2">
        {subs.map((s) => {
          const disabled = isSubDisabled(s);
          const active = !disabled && s.id === current?.id;
          const span = !isLong(s.title) ? "" : "sm:col-span-2";

          const priceLabel = disabled
            ? "СКОРО"
            : s.price_rub != null
            ? `${fmtMoney(s.price_rub)} ${currency}`
            : `0 ${currency}`;

          return (
            <button
              key={s.id}
              onClick={() => {
                if (!disabled) {
                  setSelId(s.id);
                  onSelectSub(s.id);
                }
              }}
              disabled={disabled}
              aria-disabled={disabled}
              className={`relative text-left rounded-2xl transition p-2 sm:p-2.5 ${span}
                ${
                  disabled
                    ? "bg-white/5 border border-white/10 text-white/50 cursor-not-allowed"
                    : active
                    ? "bg-white/12 border border-white/20 text-white ring-1 ring-white/20"
                    : "bg-white/7 hover:bg-white/10 border border-white/10 text-white/90"
                }`}
            >
              <div
                className={`font-semibold leading-tight text-[14px] sm:text-[15px] ${
                  disabled ? "line-through text-white/60" : "text-white"
                }`}
              >
                {s.title || "Подвид"}
              </div>
              <div
                className={`mt-0.5 text-[11px] sm:text-[12px] ${
                  disabled ? "text-white/60" : "text-white/75"
                }`}
              >
                {priceLabel}
              </div>

              {disabled && (
                <div className="absolute top-2 right-2 rounded-lg px-2 py-0.5 text-[10px] font-bold bg-white/10 text-white/80">
                  СКОРО
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* карточка описания + кнопка */}
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-3 text-[13px] text-white/85 flex flex-col min-h-[140px] max-h-[300px] sm:min-h-[160px] sm:max-h-[320px] mb-3">
          <div className="flex items-center gap-2 mb-2">
            <span>🎟️</span>
            <span className="font-semibold text-white">
              {kind.title} • {current?.title || "Подвид"}
            </span>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto pr-1 whitespace-pre-wrap break-words">
            {current?.description?.trim() ? (
              current.description.trim()
            ) : (
              <span className="text-white/70">Описание отсутствует.</span>
            )}
          </div>
        </div>

        <button
          onClick={() => {
            if (!current || isSubDisabled(current)) return;
            onNext();
          }}
          disabled={!current || isSubDisabled(current)}
          className={`mt-auto w-full rounded-2xl px-4 py-3 font-semibold shadow-[inset_0_1px_0_rgba(255,255,255,.08)]
            ${
              !current || isSubDisabled(current)
                ? "bg-white/8 text-white/45 cursor-not-allowed"
                : "bg-emerald-600/95 hover:bg-emerald-500 text-white"
            }`}
        >
          {isSubDisabled(current)
            ? "СКОРО"
            : `Выбрать «${current?.title || "подвид"}»`}
        </button>
      </div>
    </div>
  );
}